import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import {
  LightBulbIcon,
  HeartIcon,
  SparklesIcon,
  ChartBarIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import SEO from "../components/SEO";

export default function PromptsPage() {
  // Google Analytics
  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: "/prompts",
      title: "Prompts Page",
    });
  }, []);

  // State to manage which prompt was copied
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Categories of prompts
  const promptCategories = [
    {
      title: "Connect the Dots",
      icon: LightBulbIcon,
      description:
        "Surface unexpected or meaningful connections between your thoughts.",
      prompts: [
        {
          title:
            "What thread ties my ideas together that I might not have seen yet?",
          description:
            "Users love Echo for surfacing surprising links across topics and moments — turning raw ramblings into insight.",
        },
        {
          title:
            "What recurring concepts or themes keep showing up in different contexts?",
          description:
            "Discover patterns that reveal deeper insights about your thinking.",
        },
        {
          title:
            "Which of these scattered thoughts could build toward a bigger piece or argument?",
          description: "Turn disconnected ideas into cohesive narratives.",
        },
      ],
    },
    {
      title: "Shape the Idea",
      icon: HeartIcon,
      description:
        "Distill your rambling into something sharper — a thesis, an angle, or a core concept.",
      prompts: [
        {
          title:
            "What's the clearest, most compelling version of the idea I'm circling around?",
          description:
            "Valuable for developing books, essays, or Substack posts.",
        },
        {
          title:
            "How could this be framed to grab someone's attention right away?",
          description: "Craft ideas that resonate and engage your audience.",
        },
        {
          title:
            "What's the real argument I'm trying to make — even if I haven't said it directly?",
          description: "Uncover the core message hidden in your thoughts.",
        },
      ],
    },
    {
      title: "Build the Draft",
      icon: ChartBarIcon,
      description:
        "Turn scattered thoughts into structured, editable drafts or outlines.",
      prompts: [
        {
          title:
            "Can you sketch a rough outline based on what I've said so far?",
          description: "Scaffolding to kickstart the writing process.",
        },
        {
          title:
            "If this were a chapter or blog post, what would the structure look like?",
          description: "Organize your thoughts into a coherent framework.",
        },
        {
          title:
            "How could I group these ideas into sections that flow naturally?",
          description: "Create a logical progression for your ideas.",
        },
      ],
    },
    {
      title: "Find What's Missing",
      icon: ArrowPathIcon,
      description: "Reveal blind spots, gaps, or ideas left unexplored.",
      prompts: [
        {
          title:
            "What feels underdeveloped or left out in what I've written so far?",
          description: "A valuable mirror for self-editing.",
        },
        {
          title:
            "Is there a perspective, example, or idea I haven't considered yet?",
          description: "Expand your thinking by identifying overlooked angles.",
        },
        {
          title:
            "What feels important here, even if I haven’t found the words yet?",
          description: "Tap into intuition.",
        },
      ],
    },
    {
      title: "Reflect & Refine",
      icon: SparklesIcon,
      description: "Extract clarity, direction, and resonance from your notes.",
      prompts: [
        {
          title:
            "What mood, energy, or tone comes through most in my recent notes?",
          description: "Get grounded — revisit themes and feelings.",
        },
        {
          title:
            "Which themes have I been circling, even if I haven't named them?",
          description: "Identify the underlying patterns in your thinking.",
        },
        {
          title:
            "How is my thinking shifting or evolving on this topic over time?",
          description: "Track the development of your ideas and perspectives.",
        },
      ],
    },
    {
      title: "Unlock New Thought",
      icon: DocumentDuplicateIcon,
      description:
        "Ask yourself better questions. Use Echo to break through stuckness or generate momentum.",
      prompts: [
        {
          title:
            "What's a sharper or more interesting question I could be asking myself?",
          description:
            "Ask Echo to act like a coach or collaborator, nudging you forward with smart, personalized questions.",
        },
        {
          title:
            "Where could I push this idea further, if I weren't holding back?",
          description:
            "Challenge yourself to explore beyond your comfort zone.",
        },
        {
          title:
            "What part of this am I avoiding — and what might happen if I explored it?",
          description: "Confront and explore the edges of your thinking.",
        },
      ],
    },
  ];

  // Copy text to clipboard
  const copyToClipboard = (title: string, index: number) => {
    navigator.clipboard.writeText(title).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  return (
    <>
      <SEO
        title="Discover Your Mind's Hidden Patterns with Echo Prompts"
        description="Use Echo's AI-powered prompts to reveal deeper meanings, patterns, and possibilities hidden within your own thoughts."
        canonicalPath="/prompts"
      />
      <div>
        <PublicHeader />

        <div className="px-6 py-40 lg:py-60 lg:px-8">
          <div className="mx-auto flex xl:flex-row flex-col justify-between max-w-3xl xl:max-w-[1400px] text-base leading-7 text-gray-700">
            <div className="basis-[50%] xl:sticky top-40 h-fit">
              <h5 className="uppercase text-sm pb-2 font-semibold text-gray-500">
                Ask Echo Anything
              </h5>
              <h1 className="mt-2 leading-[1.4] lg:leading-[1.2] text-3xl sm:text-5xl font-serif">
                The real power of AI is helping you think for yourself
              </h1>
              <div className="max-w-xl">
                <p className="mt-6 text-lg sm:text-xl lg:leading-[1.6] pb-6">
                  Don't ask Echo to generate something from scratch — Use your
                  notes to develop ideas and create content that's entirely
                  yours.
                </p>
                <p>
                  Echo is an idea development notebook. Not only designed for
                  capturing and organizing thoughts, but also for investigating
                  them as well. Use this collection of prompts as inspiration
                  for different modes of investigation you can undertake with
                  your new notebook.
                </p>
              </div>
              <div className="mt-10 xl:mb-0 mb-20">
                <figure className="flex flex-auto flex-col justify-between border border-gray-200 rounded-lg pt-6 pb-6 pr-6 pl-12">
                  <blockquote className="text-lg/7 text-gray-900 relative">
                    <p>
                      <span className="absolute left-[-25px] top-0 text-4xl font-serif text-gray-400">
                        “
                      </span>
                      When I'm developing ideas—for example, my approach to
                      teaching—I use Echo to sketch a broad outline, then
                      gradually refine it by adding details. It's like getting
                      my thoughts 'juiced up' into a clear plan "
                    </p>
                  </blockquote>
                  <figcaption className="mt-8 flex items-center gap-x-4">
                    <img
                      alt="Tom Martin"
                      src="/headshot-tommartin.png"
                      className="size-14 rounded-full bg-gray-50"
                    />
                    <div className="text-base">
                      <div className="font-semibold text-gray-900">
                        Tom Martin
                      </div>
                      <div className="text-gray-500">
                        <a
                          href="https://x.com/lawdroid"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-gray-700"
                        >
                          @lawdroid
                        </a>
                      </div>
                    </div>
                  </figcaption>
                </figure>
              </div>
            </div>

            <div className="basis-[40%]">
              {promptCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="space-y-2 mt-10 first:mt-0">
                  <h2 className="text-xl font-serif text-gray-900 flex items-center gap-2">
                    <category.icon className="h-5 w-5 text-gray-500" />
                    {category.title}
                  </h2>
                  <p className="text-lg text-gray-600 pb-4">
                    {category.description}
                  </p>

                  <ul>
                    {category.prompts.map((prompt, promptIndex) => {
                      const globalIndex =
                        promptCategories
                          .slice(0, categoryIndex)
                          .reduce((acc, cat) => acc + cat.prompts.length, 0) +
                        promptIndex;

                      return (
                        <li
                          key={promptIndex}
                          className={`flex items-center justify-between gap-x-6 py-6 px-4 bg-white ${
                            promptIndex === 0
                              ? "rounded-t-lg border-t border-l border-r"
                              : ""
                          } ${
                            promptIndex === category.prompts.length - 1
                              ? "rounded-b-lg border-b border-l border-r"
                              : "border-l border-r"
                          } border-gray-200 first:border-t border-b`}
                        >
                          <div className="min-w-0">
                            <p className="text-m font-semibold leading-6 text-gray-900">
                              {prompt.title}
                            </p>
                            <p className="mt-1 text-sm text-gray-500">
                              {prompt.description}
                            </p>
                          </div>
                          <button
                            onClick={() =>
                              copyToClipboard(prompt.title, globalIndex)
                            }
                            className="flex-none rounded-md bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          >
                            {copiedIndex === globalIndex ? (
                              <span>Copied!</span>
                            ) : (
                              <DocumentDuplicateIcon
                                className="h-5 w-5 text-gray-500"
                                aria-hidden="true"
                              />
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
